import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { OneLineInput } from '../⚛️atoms/OneLineInput';
import { useDebounce } from 'react-use';
import { Checkbox } from '../⚛️atoms/Checkbox';
import toast from 'react-hot-toast';
import { getDataAPI, patchDataAPI } from '../../Helpers/FetchAPI';
import { ONE_TOAST_ID } from '../../constant';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { MarkdownWrapper } from './MarkdownWrapper';
import useLockedTranslate from '../../Hooks/useLockedTranslate';
import { getTranslation, updateTranslation } from '../../utils/translationHelper';
import { TranslateButton } from '../atoms/translateButton';
import { cloneWorkbookV2Value } from './editorUtils';
import {
  buildWorkbookItemSavePayload,
  getItemByTemplateApiPath,
  getTemplateItemId,
  WORKBOOK_V2_ANSWER_SAVE_DEBOUNCE_MS
} from '../../utils/workbookV2';

export const WorkbookV2Rating = forwardRef(({ item, updateItem, editing, editor, oldAnswer, editingLanguage, workbook, creditId, lessonId }, ref) => {
  const [answer, setAnswer] = useState(Number(item.answer) || 0);
  const [isCorrect, setIsCorrect] = useState(true);
  const oldQuestionInitializedRef = useRef(false);
  const { lang } = useLockedTranslate(workbook);

  const optionTranslations = item.options?.translations ?? {};
  const dummyArray = useMemo(() => {
    const values = [];
    const start = Number(item.options?.scale_start ?? 0);
    const end = Number(item.options?.scale_end ?? 10);

    for (let index = start; index < end; index += 1) {
      values.push(index);
    }

    return values;
  }, [item.options?.scale_end, item.options?.scale_start]);

  const updateEditorItem = (updates) => {
    const nextItem = cloneWorkbookV2Value(item);
    nextItem.text = updates.text ?? nextItem.text;
    nextItem.page_number = updates.page_number ?? nextItem.page_number;
    nextItem.old_question_id = updates.old_question_id ?? nextItem.old_question_id;
    nextItem.translations = updates.translations ?? nextItem.translations;
    nextItem.options = {
      ...(nextItem.options ?? {}),
      ...(updates.options ?? {})
    };
    updateItem(nextItem);
  };

  useEffect(() => {
    if (oldAnswer?.length) {
      setAnswer(Number(oldAnswer[0]) || 0);
    }
  }, [oldAnswer]);

  useDebounce(
    () => {
      if (!editing) {
        return;
      }

      if (!oldQuestionInitializedRef.current) {
        oldQuestionInitializedRef.current = true;
        return;
      }

      if (item.old_question_id) {
        toast.promise(
          getDataAPI(`/workbookItems/question/${item.old_question_id}`).then((response) => {
            const question = response.data.questions.find((currentQuestion) => currentQuestion._id === item.old_question_id);
            if (question.type === 'rating') {
              updateEditorItem({
                text: question.title,
                options: {
                  label_start: question.descLow,
                  label_end: question.descHigh
                }
              });
            }
            return question;
          }),
          {
            loading: 'Getting Old Question Data',
            success: (question) => `The old Question was a ${question.type}`,
            error: 'Error getting old question data'
          },
          { id: 'old-question' }
        );
      }
    },
    250,
    [editing, item.old_question_id]
  );

  useDebounce(
    () => {
      if (editor) {
        return;
      }
      toast.promise(
        patchDataAPI(
          getItemByTemplateApiPath(creditId, getTemplateItemId(item)),
          buildWorkbookItemSavePayload({ answer, lessonId })
        ),
        {
          loading: 'Saving...',
          success: 'Saved',
          error: 'Error saving'
        },
        { id: ONE_TOAST_ID }
      );
    },
    WORKBOOK_V2_ANSWER_SAVE_DEBOUNCE_MS,
    [answer, creditId, editor, item, lessonId]
  );

  useImperativeHandle(ref, () => ({
    checkAnswers() {
      if (!item.options?.required) {
        return true;
      }
      setIsCorrect(!!answer);
      return !!answer;
    }
  }));

  const handleAnswer = (index) => {
    const scaleEnd = Number(item.options?.scale_end ?? 10);

    if (scaleEnd - index === answer) {
      setAnswer(0);
    } else {
      setAnswer(scaleEnd - index);
    }
  };

  return (
    <div className={`flex w-full items-center gap-2 ${editing && 'border-pdteal rounded-lg border-2 px-8 py-12'}`}>
      {!editing && (
        <div className="relative flex w-full flex-col gap-2">
          <div
            className={`bg-pdred/80 absolute bottom-[4%] -left-5 w-1.5 ${isCorrect ? 'h-[10%] opacity-0' : 'h-[94%] opacity-100'} transition-all duration-300 ease-out`}
          >
            <XMarkIcon className="bg-pdred/80 absolute top-0 -left-7 size-7 rounded-l-md pl-0.5 text-white" />
          </div>
          <span>
            <MarkdownWrapper>{lang === 'en' ? item.text : getTranslation(item.translations, lang, 'text', item.text)}</MarkdownWrapper>
          </span>
          <div className="relative flex w-3/4 flex-row-reverse justify-center self-center pt-10">
            <span className="absolute top-0 left-0 -translate-x-1/2 text-2xl text-gray-500">
              {lang === 'en' ? item.options?.label_start : getTranslation(optionTranslations, lang, 'labelStart', item.options?.label_start)}
            </span>
            {dummyArray.map((value, index) => (
              <i
                key={`${value}-${index}`}
                className="peer peer-hover:bg-pdteal hover:bg-pddarkblue data-[filled=true]:bg-pdteal size-12 rounded-full border-4 border-white inset-ring-1 inset-ring-gray-200 peer-hover:inset-ring-0 hover:cursor-pointer hover:inset-ring-0"
                data-filled={Number(item.options?.scale_end ?? 10) - index <= answer}
                onClick={() => handleAnswer(index)}
              ></i>
            ))}
            <span className="absolute top-0 right-0 translate-x-1/2 text-2xl text-gray-500">
              {lang === 'en' ? item.options?.label_end : getTranslation(optionTranslations, lang, 'labelEnd', item.options?.label_end)}
            </span>
          </div>
        </div>
      )}
      {editing && (
        <div className="grid w-full grid-cols-2 gap-8">
          <div className="relative">
            <OneLineInput value={item.text} onChange={(value) => updateEditorItem({ text: value })} label="Label" />
            {editingLanguage && (
              <TranslateButton
                text={item.text}
                language={editingLanguage}
                onResponse={(value) =>
                  updateEditorItem({
                    translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                  })
                }
              />
            )}
          </div>
          {editingLanguage && (
            <OneLineInput
              value={getTranslation(item.translations, editingLanguage, 'text')}
              onChange={(value) =>
                updateEditorItem({
                  translations: updateTranslation(item.translations ?? {}, editingLanguage, 'text', value)
                })
              }
              label={`Label (${editingLanguage})`}
            />
          )}
          <OneLineInput
            className="col-start-1"
            value={item.options?.scale_start ?? 0}
            onChange={(value) => updateEditorItem({ options: { scale_start: value } })}
            label="Scale Start"
            type="number"
          />
          <OneLineInput
            value={item.options?.scale_end ?? 10}
            onChange={(value) => updateEditorItem({ options: { scale_end: value } })}
            label="Scale End"
            type="number"
          />
          <div className="relative">
            <OneLineInput
              label="Start Label"
              value={item.options?.label_start ?? 'Low'}
              onChange={(value) => updateEditorItem({ options: { label_start: value } })}
            />
            {editingLanguage && (
              <TranslateButton
                text={item.options?.label_start ?? 'Low'}
                language={editingLanguage}
                onResponse={(value) =>
                  updateEditorItem({
                    options: {
                      translations: updateTranslation(optionTranslations, editingLanguage, 'labelStart', value)
                    }
                  })
                }
              />
            )}
          </div>
          {editingLanguage && (
            <OneLineInput
              value={getTranslation(optionTranslations, editingLanguage, 'labelStart')}
              onChange={(value) =>
                updateEditorItem({
                  options: {
                    translations: updateTranslation(optionTranslations, editingLanguage, 'labelStart', value)
                  }
                })
              }
              label={`Start Label (${editingLanguage})`}
            />
          )}
          <div className="relative">
            <OneLineInput
              label="End Label"
              value={item.options?.label_end ?? 'High'}
              onChange={(value) => updateEditorItem({ options: { label_end: value } })}
            />
            {editingLanguage && (
              <TranslateButton
                text={item.options?.label_end ?? 'High'}
                language={editingLanguage}
                onResponse={(value) =>
                  updateEditorItem({
                    options: {
                      translations: updateTranslation(optionTranslations, editingLanguage, 'labelEnd', value)
                    }
                  })
                }
              />
            )}
          </div>
          {editingLanguage && (
            <OneLineInput
              value={getTranslation(optionTranslations, editingLanguage, 'labelEnd')}
              onChange={(value) =>
                updateEditorItem({
                  options: {
                    translations: updateTranslation(optionTranslations, editingLanguage, 'labelEnd', value)
                  }
                })
              }
              label={`End Label (${editingLanguage})`}
            />
          )}
          <div className="w-2/5">
            <OneLineInput
              value={item.page_number}
              label="Page Number"
              onChange={(value) => updateEditorItem({ page_number: value })}
            />
          </div>
          <OneLineInput
            value={item.old_question_id}
            label="Old Question ID"
            onChange={(value) => updateEditorItem({ old_question_id: value })}
          />
          <div className="w-1/5">
            <Checkbox
              label="Required"
              checked={item.options?.required ?? false}
              onChange={(value) => updateEditorItem({ options: { required: value } })}
            />
          </div>
        </div>
      )}
    </div>
  );
});
